import { useCallback, useEffect, useState, type FormEvent } from "react";
import type { StudentBillingLessonFeeResponse } from "../../shared/api/studentBillingConfiguration";
import {
  fetchStudentBillingLessonFee,
  fetchStudentBillingLessonFees,
  fetchStudentBillingLessonMigration,
  putStudentBillingLessonFee,
  type StudentBillingLessonFeeListResponse
} from "../../shared/api/studentBillingConfiguration";
import type { StudentBillingLessonMigrationComparisonDto } from "@madamuh/domain";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isUnauthenticatedApiError } from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import {
  BillingConfigurationNav,
  BillingStatusBadge,
  billingButtonStyle,
  billingCardStyle,
  billingGridStyle,
  billingInputStyle,
  billingMutedStyle,
  billingPageLinks,
  formatBillingDate,
  formatBillingYen,
  studentBillingConfigurationError
} from "./AdminBillingConfigurationShared";

function today(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

function lessonKindLabel(kind: "regular" | "shortTerm"): string {
  return kind === "regular" ? "レギュラーレッスン" : "単発・短期レッスン";
}

function lessonAmountLabel(kind: "regular" | "shortTerm", amount: number): string {
  return `${formatBillingYen(amount)}${kind === "regular" ? " / 月" : " / コース総額"}`;
}

function LessonFeeSummary({ item, tenantSlug }: { item: StudentBillingLessonFeeListResponse["items"][number]; tenantSlug: string }) {
  return (
    <article style={billingCardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start", flexWrap: "wrap" }}>
        <div style={{ display: "grid", gap: 5 }}>
          <Link to={`/t/${tenantSlug}/teacher/admin/billing/lesson-fees/${item.lessonId}`}><strong>{item.lessonTitle}</strong></Link>
          <span style={billingMutedStyle}>{lessonKindLabel(item.lessonKind)}</span>
        </div>
        {!item.configured ? (
          <BillingStatusBadge tone="warning">未設定</BillingStatusBadge>
        ) : item.current?.amount === 0 ? (
          <BillingStatusBadge tone="success">無料（0円）</BillingStatusBadge>
        ) : item.current ? (
          <BillingStatusBadge tone="success">設定済み</BillingStatusBadge>
        ) : (
          <BillingStatusBadge>適用期間外</BillingStatusBadge>
        )}
      </div>
      {item.current ? (
        <>
          <strong>{lessonAmountLabel(item.lessonKind, item.current.amount)}</strong>
          <p style={billingMutedStyle}>適用期間: {formatBillingDate(item.current.effectiveFrom)}〜{item.current.effectiveTo ? formatBillingDate(item.current.effectiveTo) : "終了日なし"}</p>
        </>
      ) : <p style={billingMutedStyle}>現在適用中の料金はありません。</p>}
    </article>
  );
}

export function AdminBillingLessonFeesPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const [response, setResponse] = useState<StudentBillingLessonFeeListResponse | null>(null);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [safeError, setSafeError] = useState<string | null>(null);

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    let active = true;
    setLoadState("loading");
    setSafeError(null);
    void fetchStudentBillingLessonFees(tenantSlug)
      .then((value) => { if (active) { setResponse(value); setLoadState("idle"); } })
      .catch((error: unknown) => {
        if (!active) return;
        if (isUnauthenticatedApiError(error)) { navigate(`/t/${tenantSlug}/entry`, { replace: true }); return; }
        setSafeError(studentBillingConfigurationError(error, "レッスン料金設定を取得できませんでした。"));
        setLoadState("error");
      });
    return () => { active = false; };
  }, [hasAdminRole, isSessionReady, navigate, tenantSlug]);

  return (
    <OperatorPageScaffold currentArea="admin" tenantSlug={tenantSlug} tenantName={session?.tenant.displayName} session={session}
      title="レッスン料金設定" description="レッスンを請求金額の基準として設定します。" links={billingPageLinks} layoutVariant="wide">
      <div style={billingGridStyle}>
        <BillingConfigurationNav tenantSlug={tenantSlug} />
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>レッスン料金設定を読み込んでいます。</p> : null}
        {safeError ? <p role="alert">{safeError}</p> : null}
        {response ? (
          <>
            {!response.capabilities.canManageFeeMasters ? <p style={billingMutedStyle}>閲覧のみです。料金を変更できるのはテナントオーナーです。</p> : null}
            {response.items.length === 0 ? <p>対象レッスンはありません。</p> : null}
            <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}>
              {response.items.map((item) => <LessonFeeSummary key={item.lessonId} item={item} tenantSlug={tenantSlug} />)}
            </div>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}

export function AdminBillingLessonFeeDetailPage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant", lessonId = "" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const [detail, setDetail] = useState<StudentBillingLessonFeeResponse | null>(null);
  const [migration, setMigration] = useState<StudentBillingLessonMigrationComparisonDto | null>(null);
  const [amount, setAmount] = useState("");
  const [effectiveFrom, setEffectiveFrom] = useState(today());
  const [effectiveTo, setEffectiveTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [safeError, setSafeError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setSafeError(null);
    try {
      const [next, migrationResponse] = await Promise.all([
        fetchStudentBillingLessonFee(tenantSlug, lessonId),
        fetchStudentBillingLessonMigration(tenantSlug, lessonId)
      ]);
      setDetail(next);
      setMigration(migrationResponse.comparison);
      setAmount(next.current ? String(next.current.amount) : "");
      setEffectiveFrom(next.current?.effectiveFrom ?? today());
      setEffectiveTo(next.current?.effectiveTo ?? "");
    } catch (error) {
      if (isUnauthenticatedApiError(error)) { navigate(`/t/${tenantSlug}/entry`, { replace: true }); return; }
      setSafeError(studentBillingConfigurationError(error, "レッスン料金設定を取得できませんでした。"));
    }
  }, [lessonId, navigate, tenantSlug]);

  useEffect(() => { if (isSessionReady && hasAdminRole) void load(); }, [hasAdminRole, isSessionReady, load]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!detail) return;
    setBusy(true);
    setSafeError(null);
    setMessage(null);
    try {
      const parsedAmount = Number(amount);
      if (!Number.isSafeInteger(parsedAmount) || parsedAmount < 0) throw new Error("invalid amount");
      await putStudentBillingLessonFee(tenantSlug, lessonId, {
        effectiveFrom,
        effectiveTo: effectiveTo || null,
        amount: parsedAmount,
        currency: "JPY",
        expectedVersion: detail.history.find((setting) => setting.effectiveFrom === effectiveFrom)?.version ?? null
      });
      setMessage("レッスン料金設定を保存しました。");
      await load();
    } catch (error) {
      setSafeError(studentBillingConfigurationError(error, "金額と適用期間を確認してください。"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <OperatorPageScaffold currentArea="admin" tenantSlug={tenantSlug} tenantName={session?.tenant.displayName} session={session}
      title="レッスン料金の詳細" description="現在の料金、適用期間、変更履歴を確認します。" links={billingPageLinks} layoutVariant="wide">
      <div style={billingGridStyle}>
        <BillingConfigurationNav tenantSlug={tenantSlug} />
        <Link to={`/t/${tenantSlug}/teacher/admin/billing/lesson-fees`}>レッスン料金設定へ戻る</Link>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {safeError ? <p role="alert">{safeError}</p> : null}
        {message ? <p role="status">{message}</p> : null}
        {detail ? (
          <>
            <section style={billingCardStyle}>
              <h2 style={{ margin: 0 }}>{detail.lessonTitle}</h2>
              <p style={billingMutedStyle}>{lessonKindLabel(detail.lessonKind)}</p>
              {!detail.configured ? <BillingStatusBadge tone="warning">未設定</BillingStatusBadge> : null}
              {detail.current ? <strong>{lessonAmountLabel(detail.lessonKind, detail.current.amount)}</strong> : <p style={billingMutedStyle}>現在適用中の料金はありません。</p>}
              {detail.current?.firstBillableServiceMonthPreview ? <p style={billingMutedStyle}>初回請求対象月（参考）: {detail.current.firstBillableServiceMonthPreview}</p> : null}
              <Link to={`/t/${tenantSlug}/teacher/admin/lessons/${detail.lessonId}`}>レッスン基本情報を開く</Link>
            </section>

            <section style={billingCardStyle}>
              <h2 style={{ margin: 0 }}>現行MVP設定との比較</h2>
              <p style={billingMutedStyle}>比較結果は参考情報です。自動保存や自動移行は行いません。</p>
              {migration?.status === "noCurrentMvpData" ? <p>比較対象の現行MVP料金はありません。</p> : null}
              {migration && migration.status !== "noCurrentMvpData" ? <p>対象生徒: {migration.enrolledStudentCount}名 / 現行MVP設定済み: {migration.configuredStudentCount}名</p> : null}
              {migration?.status === "uniform" ? <p>受講者ごとの現行MVP料金は統一されています。候補: {migration.suggestedLessonAmount === null ? "なし" : formatBillingYen(migration.suggestedLessonAmount)}</p> : null}
              {migration?.status === "mixed" ? <p>受講者ごとの現行MVP料金が混在しています。内容を確認して手入力してください。</p> : null}
              {migration?.distinctAmounts.map((entry) => <p key={entry.amount} style={billingMutedStyle}>{formatBillingYen(entry.amount)}: {entry.studentCount}名</p>)}
            </section>

            {detail.capabilities.canManageFeeMasters ? (
              <form onSubmit={(event) => void save(event)} style={billingCardStyle}>
                <h2 style={{ margin: 0 }}>料金を設定</h2>
                <label style={billingGridStyle}><span>金額（円）</span><input aria-label="レッスン料金（円）" type="number" min="0" step="1" required value={amount} onChange={(event) => setAmount(event.target.value)} style={billingInputStyle} /></label>
                <p style={billingMutedStyle}>0円は「無料」として保存されます。通貨は JPY 固定です。</p>
                <div style={{ display: "grid", gap: 12, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
                  <label style={billingGridStyle}><span>適用開始日</span><input type="date" required value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} style={billingInputStyle} /></label>
                  <label style={billingGridStyle}><span>適用終了日（任意）</span><input type="date" value={effectiveTo} onChange={(event) => setEffectiveTo(event.target.value)} style={billingInputStyle} /></label>
                </div>
                <p style={billingMutedStyle}>適用期間の切替は1回の保存で自動調整しません。既存期間と重ならない日付を指定してください。</p>
                <button type="submit" disabled={busy} style={billingButtonStyle}>{busy ? "保存中..." : "料金設定を保存"}</button>
              </form>
            ) : <p style={billingMutedStyle}>閲覧のみです。料金を変更できるのはテナントオーナーです。</p>}

            <section style={billingCardStyle}>
              <h2 style={{ margin: 0 }}>料金設定履歴</h2>
              {detail.history.length === 0 ? <p style={billingMutedStyle}>履歴はありません。</p> : detail.history.map((setting) => (
                <article key={setting.settingId} style={{ borderTop: "1px solid #e5e7eb", paddingTop: 10, display: "grid", gap: 4 }}>
                  <strong>{lessonAmountLabel(setting.lessonKind, setting.amount)}</strong>
                  <span style={billingMutedStyle}>適用期間: {formatBillingDate(setting.effectiveFrom)}〜{setting.effectiveTo ? formatBillingDate(setting.effectiveTo) : "終了日なし"}</span>
                  <span style={billingMutedStyle}>状態: {setting.status === "active" ? "有効" : "終了"} / version {setting.version}</span>
                </article>
              ))}
            </section>
          </>
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
